import { useApi } from "./useApi";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import * as z from "zod";

const logsMetadataBlobChunkSchema = z.object({
  chunk: z.number(),
  url: z.string(),
  lines: z.number(),
  size_bytes: z.number(),
});

const logsMetadataBlobsSchema = z.union([
  // Legacy format: array of arrays (step-indexed), transformed into a record
  z.array(z.array(logsMetadataBlobChunkSchema)).transform((arr) => {
    const record: Record<
      string,
      Array<z.infer<typeof logsMetadataBlobChunkSchema>>
    > = {};
    arr.forEach((chunks, index) => {
      record[index.toString()] = chunks;
    });
    return record;
  }),
  // New format: already a record keyed by step/record_order (as string)
  z.record(z.string(), z.array(logsMetadataBlobChunkSchema)),
]);

const logsMetadataEntrySchema = z.object({
  job_id: z.string(),
  total_lines: z.number(),
  total_steps: z.number(),
  blobs: logsMetadataBlobsSchema,
  created_at: z.string(),
});

/** New API shape: job_id + logfiles[type][name] */
const logsMetadataNewSchema = z.object({
  job_id: z.string(),
  logfiles: z.record(z.string(), z.record(z.string(), logsMetadataEntrySchema)),
});

/** Legacy API shape: flat object with blobs at top level */
const logsMetadataLegacySchema = z.object({
  job_id: z.string().optional(),
  total_lines: z.number(),
  total_steps: z.number(),
  blobs: logsMetadataBlobsSchema,
  created_at: z.string(),
});

/** Parse both shapes and normalize to new shape for consistent consumption */
const logsMetadataSchema = z
  .union([logsMetadataNewSchema, logsMetadataLegacySchema])
  .transform((val) => {
    if ("logfiles" in val && val.logfiles != null) {
      return val as z.infer<typeof logsMetadataNewSchema>;
    }
    const legacy = val as z.infer<typeof logsMetadataLegacySchema>;
    return {
      job_id: legacy.job_id ?? "",
      logfiles: {
        stdout: {
          "github-action": {
            job_id: legacy.job_id ?? "",
            total_lines: legacy.total_lines,
            total_steps: legacy.total_steps,
            blobs: legacy.blobs,
            created_at: legacy.created_at,
          },
        },
      },
    };
  });

// Schema for a workflow job step
const jobStepSchema = z.object({
  name: z.string(),
  status: z.string(),
  number: z.number(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  conclusion: z.string().nullable(),
});

// Schema for a workflow job
export const workflowJobSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  status: z.string().nullable(),
  conclusion: z.string().nullable(),
  event_status: z.string(),
  runtime_seconds: z
    .number()
    .nullable()
    .transform((v) => v ?? 0),
  workflow_name: z.string(),
  repository_name: z.string(),
  workflow_run_id: z
    .union([z.number(), z.string()])
    .transform((val) => (typeof val === "number" ? val.toString() : val)),
  workflow_run_attempt: z.number(),
  labels: z.array(z.string()),
  steps: z.array(jobStepSchema).nullable(),
  job_url: z.string(),
  runner_name: z.string().nullable().optional(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  junit_tests_blobs: z.array(z.string()).nullable().optional(),
  parsed_tests_blobs: z.array(z.string()).nullable().optional(),
  logs_metadata: logsMetadataSchema
    .nullable()
    .optional()
    .catch((ctx) => {
      console.warn(
        "[useRunDetails] logs_metadata did not match expected schema, falling back to null.",
        { input: ctx.input, error: ctx.error.issues },
      );
      return null;
    }),
});

// Schema for a run attempt
const runAttemptSchema = z.object({
  attempt: z.number(),
  id: z.number(),
  name: z.string(),
  status: z.string().nullable(),
  event: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  html_url: z.string(),
});

// Schema for a pull request linked to a workflow run
const runPullRequestSchema = z.object({
  number: z.number(),
  html_url: z.string(),
});

// Schema for the response from the jobs API
export const workflowRunDetailsResponseSchema = z.object({
  run_id: z.string(),
  workflow_name: z.string(),
  repository_name: z.string(),
  jobs: z.array(workflowJobSchema),
  attempts: z.array(runAttemptSchema),
  head_branch: z.string().nullish(),
  pull_request: runPullRequestSchema.nullish(),
});

export type WorkflowRunJob = z.infer<typeof workflowJobSchema>;
export type RunAttempt = z.infer<typeof runAttemptSchema>;
export type WorkflowRunDetailsResponse = z.infer<
  typeof workflowRunDetailsResponseSchema
>;

// Function to fetch workflow run jobs (returns all attempts)
async function fetchRunDetails(
  api: ReturnType<typeof useApi>,
  org: string,
  runId: string,
): Promise<WorkflowRunDetailsResponse> {
  const url = `user/github/orgs/${org}/metrics/actions/workflows/runs/${runId}`;
  const response = await api.get(url);
  return workflowRunDetailsResponseSchema.parse(response.data);
}

// Hook to fetch all jobs for a workflow run across all attempts
function useRunDetails(runId: string) {
  const api = useApi();
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const queryKey = useMemo(
    () => ["workflowRunJobs", activeGithubOrg || "", runId],
    [activeGithubOrg, runId],
  );

  return useQuery(
    queryKey,
    async () => {
      if (!activeGithubOrg || !runId) {
        return null;
      }
      // Fetch all run attempts at once
      return await fetchRunDetails(api, activeGithubOrg, runId);
    },
    {
      enabled: !!activeGithubOrg && !!runId,
      suspense: false,
    },
  );
}

// Enhanced hook that includes current attempt selection functionality
export function useRunDetailsByAttempt(runId: string, initialAttempt?: number) {
  const { data, isLoading, error } = useRunDetails(runId);
  const [selectedAttempt, setSelectedAttempt] = useState<number | null>(null);

  // The drawer keeps this hook mounted across clicks, so a new requested
  // run/attempt must clear the retained selection; manual selection inside
  // RunDetails leaves the request unchanged and is preserved.
  const requestKey = `${runId}:${initialAttempt ?? ""}`;
  const [appliedRequestKey, setAppliedRequestKey] = useState(requestKey);
  if (requestKey !== appliedRequestKey) {
    setAppliedRequestKey(requestKey);
    setSelectedAttempt(null);
  }

  // Compute available attempt numbers
  const availableAttempts = useMemo(() => {
    if (!data?.attempts) return [];
    return data.attempts
      .map((a) => a.attempt)
      .sort()
      .reverse(); // Sort descending
  }, [data]);

  // Once data loads, select the requested attempt if it exists, else the
  // most recent; the choice then sticks until the request changes.
  if (availableAttempts.length && selectedAttempt === null) {
    setSelectedAttempt(
      initialAttempt && availableAttempts.includes(initialAttempt)
        ? initialAttempt
        : availableAttempts[0],
    );
  }

  // Find the current attempt data
  const currentAttemptData = useMemo(() => {
    if (!data?.attempts || selectedAttempt === null) return null;

    return (
      data.attempts.find((attempt) => attempt.attempt === selectedAttempt) ||
      null
    );
  }, [data, selectedAttempt]);

  // Get jobs for the current attempt, including implicitly applicable jobs from previous attempts
  const runJobs = data?.jobs;
  const currentAttemptJobs = useMemo(() => {
    if (!runJobs || selectedAttempt === null) return [];

    // Get all jobs up to and including the current attempt
    const relevantJobs = runJobs.filter((job) => {
      // Skip jobs with null attempt numbers
      if (job.workflow_run_attempt === null) return false;
      return job.workflow_run_attempt <= selectedAttempt;
    });

    // Group by job.name. The dashboard has to handle GitHub's re-run model
    // entirely client-side (the API returns every job from every attempt of
    // the run): only failed jobs are re-run, and successful jobs from
    // earlier attempts are implicitly carried forward into later attempts.
    // We can't dedup by job.id because the carry-over is identified by name
    // — but we also can't collapse by name alone, because matrix shards can
    // share an identical job.name within a single attempt and all of them
    // need to render.
    //
    // Algorithm: for each name, the visible count is the max number of
    // jobs with that name seen in any single attempt (= the matrix width
    // for matrix jobs, or 1 for simple jobs). Walk attempts from
    // selectedAttempt downward, taking jobs and stopping once we've
    // collected that many. Within each attempt we prefer non-failure
    // conclusions first so that, on a partial re-run, the
    // carried-forward shards are the ones that succeeded in the prior
    // attempt (the failures are the ones GH re-runs, and those re-runs
    // are already in the higher-attempt bucket).
    const byName = new Map<string, WorkflowRunJob[]>();
    for (const job of relevantJobs) {
      const arr = byName.get(job.name);
      if (arr) {
        arr.push(job);
      } else {
        byName.set(job.name, [job]);
      }
    }

    const isFailureLike = (conclusion: string | null | undefined) =>
      conclusion === "failure" ||
      conclusion === "cancelled" ||
      conclusion === "timed_out";

    const result: WorkflowRunJob[] = [];
    byName.forEach((jobsForName) => {
      let expected = 0;
      const countByAttempt = new Map<number, number>();
      for (const j of jobsForName) {
        const a = j.workflow_run_attempt ?? 0;
        const next = (countByAttempt.get(a) ?? 0) + 1;
        countByAttempt.set(a, next);
        if (next > expected) expected = next;
      }

      // Sort desc by attempt; within an attempt, non-failure first.
      const sorted = [...jobsForName].sort((a, b) => {
        const aa = a.workflow_run_attempt ?? 0;
        const bb = b.workflow_run_attempt ?? 0;
        if (aa !== bb) return bb - aa;
        const aFail = isFailureLike(a.conclusion) ? 1 : 0;
        const bFail = isFailureLike(b.conclusion) ? 1 : 0;
        return aFail - bFail;
      });

      for (let i = 0; i < Math.min(expected, sorted.length); i++) {
        result.push(sorted[i]);
      }
    });

    return result;
  }, [runJobs, selectedAttempt]);

  return {
    data,
    currentAttempt: currentAttemptData,
    currentAttemptJobs,
    isLoading,
    error,
    selectedAttempt,
    setSelectedAttempt,
    availableAttempts,
  };
}

// Hook for prefetching run details
export function usePrefetchRunDetails() {
  const queryClient = useQueryClient();
  const api = useApi();
  const { activeGithubOrg } = useActiveGithubOrgForUser();

  const prefetchRunDetails = useCallback(
    async (runId: string) => {
      if (!activeGithubOrg || !runId) return;

      const queryKey = ["workflowRunJobs", activeGithubOrg, runId];

      // Only prefetch if the data isn't already in the cache
      const existingData = queryClient.getQueryData(queryKey);
      if (existingData) return;

      // Prefetch the data
      await queryClient.prefetchQuery(
        queryKey,
        async () => {
          return await fetchRunDetails(api, activeGithubOrg, runId);
        },
        {
          // Keep the prefetched data for 5 minutes
          staleTime: 5 * 60 * 1000,
        },
      );
    },
    [activeGithubOrg, api, queryClient],
  );

  return prefetchRunDetails;
}
